import { Component, EventEmitter, forwardRef, Input, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { TableColumn, ToastConfig } from '@app/shared';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { combineLatest, take } from 'rxjs';
import { GlobalValuesService, LocationTransportService, ScreenService, UnitOfMeasureService } from '@app/core';
import { MaestrosUtilsService } from '../../../features/maestros/services/maestros-utils.service';
import { NzNotificationRef, NzNotificationService } from 'ng-zorro-antd/notification';
import { MasterFormDrawerComponent } from '../master-form-drawer/master-form-drawer.component';

const CREATE_NEW_VALUE = 'CREATE_NEW';

export type MasterSelectType = 'unitOfMeasure' | 'location';

interface MasterSelectConfig {
  getItems: () => any[];
  normalize: (payload: any) => any;
  create: (payload: any) => any;
  updateCache: (createdItem: any) => void;
  successMessage: string;
  errorMessage: string;
}

@Component({
  selector: 'app-master-select',
  standalone: false,
  templateUrl: './master-select.component.html',
  styleUrl: './master-select.component.less',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MasterSelectComponent),
      multi: true,
    },
  ],
})
export class MasterSelectComponent implements ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() placeholder = 'Selecciona una opcion...';
  @Input() labelProperty = 'name';
  @Input() valueProperty = 'id';
  @Input() nzAllowClear = true;
  @Input() nzLoading = false;
  @Input() nzStatus?: 'error' | 'warning' | '';
  @Input() allowCreateNew = false;
  @Input() masterType?: MasterSelectType;
  @Input() createLabel = 'Agregar';
  @Input() createPlaceholder = 'nuevo...';
  @Input() createDefaultValues: Record<string, any> = {};
  @Input() noneLabel?: string;
  @Input() noneValue: number | null = 0;
  @Output() createdItem = new EventEmitter<any>();

  @ViewChild('notificationTmpl') notificationTmpl?: TemplateRef<any>;

  private drawerService = inject(NzDrawerService);
  private screenService = inject(ScreenService);
  private maestrosUtilsService = inject(MaestrosUtilsService);
  private unitOfMeasureService = inject(UnitOfMeasureService);
  private locationTransportService = inject(LocationTransportService);
  private globalValuesService = inject(GlobalValuesService);
  private notificationService = inject(NzNotificationService);

  value: number | null = null;
  disabled = false;
  searchValue = '';
  private createDrawerRef?: NzDrawerRef;
  notRef?: NzNotificationRef;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: '',
    showUndo: false,
  };

  onChange: (value: number | null) => void = () => {};
  onTouched: () => void = () => {};

  get createNewSearchText(): string {
    const text = this.searchValue.trim();
    return text ? `"${text}"...` : this.createPlaceholder;
  }

  writeValue(value: number | null): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(value: any): void {
    if (value === CREATE_NEW_VALUE) {
      this.value = null;
      this.openCreateDrawer();
      return;
    }

    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  onCreateNewClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.openCreateDrawer();
  }

  onSearch(searchValue: string): void {
    this.searchValue = searchValue;
  }

  getOptionLabel(option: any): string {
    return option?.[this.labelProperty] || '';
  }

  getOptionValue(option: any): any {
    return option?.[this.valueProperty];
  }

  private openCreateDrawer(): void {
    if (!this.allowCreateNew || this.disabled || !this.masterType) {
      return;
    }

    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile }) => {
        this.createDrawerRef = this.drawerService.create({
          nzWidth: '635px',
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: MasterFormDrawerComponent,
          nzData: {
            columnsData: this.getCreateColumnsData(),
            editingElement: null,
            maestroName: this.getMaestroName(),
            defaultValues: this.createDefaultValues,
          },
        });

        this.maestrosUtilsService.drawerRef = this.createDrawerRef;

        this.createDrawerRef.afterOpen.pipe(take(1)).subscribe(() => {
          const component = this.createDrawerRef?.getContentComponent();
          this.prefillName(component);

          component?.createEvent?.pipe(take(1)).subscribe((payload: any) => this.createMasterItem(payload));
        });

        this.createDrawerRef.afterClose.pipe(take(1)).subscribe(() => {
          if (this.maestrosUtilsService.drawerRef$.value === this.createDrawerRef) {
            this.maestrosUtilsService.drawerRef = undefined;
          }
          this.createDrawerRef = undefined;
        });
      });
  }

  private prefillName(component: any): void {
    const name = this.searchValue.trim();
    const form = component?.dynamicForm;
    if (name && form?.get('name')) {
      form.patchValue({ name });
    }
  }

  private createMasterItem(payload: any): void {
    const config = this.getMasterConfig();
    if (!config) {
      return;
    }

    const normalized = config.normalize(payload);
    const existing = this.findExistingByName(config.getItems(), normalized.name);
    if (existing) {
      this.completeCreate(existing);
      return;
    }

    config.create(normalized).subscribe({
      next: (response: any) => {
        const createdItem = { ...normalized, ...(response?.data || response) };
        config.updateCache(createdItem);
        this.completeCreate(createdItem);
        this.showSuccess(config.successMessage);
      },
      error: (error: any) => {
        console.error('Error creating master item from master select', error);
        this.showError(config.errorMessage, error?.error?.message);
      },
    });
  }

  private getCreateColumnsData(): TableColumn[] {
    if (this.masterType === 'unitOfMeasure') {
      return [
        {
          name: 'Nombre',
          pathData: { name: 'name', type: 'text' },
          validators: [{ error: 'required', function: Validators.required, message: 'El Nombre es requerido' }],
        },
        {
          name: 'Factor',
          pathData: { name: 'factor', type: 'number' },
          noVisibleInTable: true,
          noVisibleInForm: false,
          defaultValue: 1,
        },
        {
          name: 'Categoria',
          pathData: { name: 'unitOfMeasureCategoryId', type: 'select' },
          optionList: this.globalValuesService.unitOfMeasureCategories$.value || [],
          defaultValue:
            this.createDefaultValues?.['unitOfMeasureCategoryId'] ||
            this.globalValuesService.unitOfMeasureCategories$.value[0]?.id,
          validators: [{ error: 'required', function: Validators.required, message: 'La categoria es requerida' }],
        },
        {
          name: 'Activa',
          pathData: { name: 'isActive', type: 'boolean' },
          defaultValue: true,
        },
      ];
    }

    return [
      {
        name: 'Nombre',
        pathData: { name: 'name', type: 'text' },
        validators: [{ error: 'required', function: Validators.required, message: 'El Nombre es requerido' }],
      },
      {
        name: 'Tipo de Localizacion',
        pathData: { name: 'locationTypeId', type: 'select', nestedPath: 'name' },
        optionList: this.globalValuesService.locationsTypes$.value || [],
        defaultValue: this.globalValuesService.locationsTypes$.value[0]?.id,
        validators: [{ error: 'required', function: Validators.required, message: 'El tipo de localizacion es requerido' }],
      },
      {
        name: 'Latitud',
        pathData: { name: 'latitude', type: 'number' },
        defaultValue: null,
        validators: [{ error: 'required', function: Validators.required, message: 'La latitud es requerida' }],
      },
      {
        name: 'Longitud',
        pathData: { name: 'longitude', type: 'number' },
        defaultValue: null,
        validators: [{ error: 'required', function: Validators.required, message: 'La longitud es requerida' }],
      },
    ];
  }

  private getMaestroName(): string {
    return this.masterType === 'unitOfMeasure' ? 'Unidad de Medida' : 'Localizacion';
  }

  private normalizeUnitOfMeasurePayload(payload: any): any {
    return {
      name: payload.name || '',
      factor: payload.factor || 1,
      isActive: payload.isActive ?? true,
      unitOfMeasureCategoryId:
        payload.unitOfMeasureCategoryId ||
        this.createDefaultValues?.['unitOfMeasureCategoryId'] ||
        this.globalValuesService.unitOfMeasureCategories$.value[0]?.id ||
        0,
    };
  }

  private normalizeLocationPayload(payload: any): any {
    return {
      name: payload.name || '',
      locationTypeId: payload.locationTypeId || this.globalValuesService.locationsTypes$.value[0]?.id || 0,
      latitude: payload.latitude || 0,
      longitude: payload.longitude || 0,
    };
  }

  private findExistingByName(items: any[], name: string): any | undefined {
    return (items || []).find((item) => item.name?.trim().toLowerCase() === name.trim().toLowerCase());
  }

  private getMasterConfig(): MasterSelectConfig | null {
    if (this.masterType === 'unitOfMeasure') {
      return {
        getItems: () => this.globalValuesService.unitOfMeasureslist || [],
        normalize: (payload: any) => this.normalizeUnitOfMeasurePayload(payload),
        create: (payload: any) => this.unitOfMeasureService.createUnitOfMeasure(payload),
        updateCache: (createdItem: any) => {
          const units = this.globalValuesService.unitOfMeasureslist || [];
          this.globalValuesService.unitOfMeasures = [...units, createdItem];
        },
        successMessage: 'Unidad de medida creada correctamente.',
        errorMessage: 'Error al crear la unidad de medida.',
      };
    }

    if (this.masterType === 'location') {
      return {
        getItems: () => this.globalValuesService.places$.value || [],
        normalize: (payload: any) => this.normalizeLocationPayload(payload),
        create: (payload: any) => this.locationTransportService.createLocation(payload),
        updateCache: (createdItem: any) => {
          const locations = this.globalValuesService.places$.value || [];
          this.globalValuesService.places$.next([...locations, createdItem]);
        },
        successMessage: 'La localizacion se ha creado.',
        errorMessage: 'Error al tratar de crear la localizacion.',
      };
    }

    return null;
  }

  private completeCreate(createdItem: any): void {
    if (!createdItem?.[this.valueProperty]) {
      return;
    }

    if (!this.options.some((option) => Number(this.getOptionValue(option)) === Number(createdItem[this.valueProperty]))) {
      this.options = [...this.options, createdItem];
    }

    this.value = createdItem[this.valueProperty];
    this.onChange(this.value);
    this.onTouched();
    this.createdItem.emit(createdItem);
    this.maestrosUtilsService.drawerRef = undefined;
    this.createDrawerRef?.close();
  }

  private showSuccess(content: string): void {
    this.toastConfig = {
      icon: 'check_circle',
      iconColor: '#6CDA75',
      content,
      showUndo: false,
    };
    this.showNotification();
  }

  private showError(content: string, msg?: string): void {
    this.toastConfig = {
      icon: 'dangerous',
      iconColor: '#CB2A2F',
      content,
      msg,
      showUndo: false,
    };
    this.showNotification();
  }

  private showNotification(): void {
    if (!this.notificationTmpl) {
      return;
    }

    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight', nzClass: 'custom-notification' }
    );
  }
}
